import {
  Content,
  ContentMetadata,
  SelectedMedia,
  Menu,
  MenuItem,
  WebsiteBlockDetail,
  Topic,
} from '@/lib/model';
import { dedupeById } from '@/utils/data/dedupeById';
import { getCoverImageUrl, getImageLoaderWidth, imageUrlLoader } from './media';

export const getOptimizedImageFromCoverImage = (
  coverImage?: string | SelectedMedia | null | undefined,
  width?: number,
  height?: number,
): string => {
  const url = getCoverImageUrl(coverImage, width, height) as string;

  return imageUrlLoader(url, 'webp', getImageLoaderWidth(width));
};

export const getImageFromCoverImage = (
  coverImage?: string | SelectedMedia | null | undefined,
): string => {
  if (coverImage) {
    return typeof coverImage === 'string'
      ? coverImage
      : coverImage?.media?.type === 'VIDEO' ||
          coverImage?.media?.type === 'AUDIO'
        ? (coverImage?.media?.thumbnailUrl ??
          coverImage?.media?.url ??
          '/png/placeholder.png')
        : coverImage?.media?.url || '/png/placeholder.png';
  }
  return '/png/placeholder.png';
};

export const getMediaIconType = (content?: Content) => {
  const isVideo = [
    'dailyVideo',
    'videoCard',
    'videoPodcast',
    'tvContent',
  ].includes(content?.subType ?? '');
  const isAudio = [
    'podcastEpisode',
    'dailyNewsBriefEpisode',
    'featureEpisode',
  ].includes(content?.subType ?? '');
  return {
    isVideo,
    isAudio,
  };
};

export function getCoverImageCaption(coverImage: SelectedMedia) {
  const source = coverImage?.source;
  const rawCaption = coverImage?.caption || '';
  const lowerCaseCaption = rawCaption?.toLowerCase();

  const hasSourceInCaption =
    lowerCaseCaption.includes('/') &&
    lowerCaseCaption.includes(source?.title?.toLowerCase() || '');

  if (hasSourceInCaption || !source?.title) {
    return rawCaption;
  }
  return `${rawCaption} / ${source?.title}`;
}
export const getContentMetadata = (content?: Content) => {
  const mobileContentMetadata = getContentMetadataByPlatform('mobile', content);
  const desktopContentMetadata = getContentMetadataByPlatform(
    'desktop',
    content,
  );
  return { mobileContentMetadata, desktopContentMetadata };
};

export const getContentMetadataByPlatform = (
  type: ContentMetadata['platform'],
  content?: Content,
): ContentMetadata => {
  const websiteBlockDetails =
    content?.websiteBlockDetails as WebsiteBlockDetail;
  const contentMetadataList =
    websiteBlockDetails?.contentMetadata as ContentMetadata[];

  // find contentMetadata by platform
  const contentMetadata = contentMetadataList?.find(item => {
    return item.platform === type;
  });
  const coverImage = content?.coverImage as SelectedMedia;
  const rootMedia = content?.media as SelectedMedia;
  const wPlusMedias =
    contentMetadata?.medias?.filter(
      m => (!!m?.media as unknown as SelectedMedia)?.media,
    ) || [];
  // w+ medias or media or cover image
  let medias = wPlusMedias?.length
    ? wPlusMedias
    : rootMedia || content?.coverImage
      ? [
          {
            media: rootMedia
              ? {
                  ...rootMedia,
                  media: {
                    ...rootMedia.media,
                    imageUrl: getOptimizedImageFromCoverImage(coverImage),
                  },
                }
              : coverImage,
          },
        ]
      : [];

  // single w+ video
  if (wPlusMedias?.length === 1 && coverImage) {
    medias = wPlusMedias.map(item => {
      if (typeof item.media === 'string' || !item.media) return item;
      const media = { ...item.media };
      if (
        item.media?.media?.type === 'VIDEO' ||
        media?.media?.type === 'AUDIO'
      ) {
        item.media = {
          media: {
            ...media?.media,
            imageUrl: getOptimizedImageFromCoverImage(coverImage),
          },
        };
      }
      return item;
    });
  }
  return {
    platform: type,
    medias: medias,
    description: contentMetadata?.description ?? content?.description,
    liveStream: contentMetadata?.liveStream,
    // liveStream: item?.title.includes('International law')
    //   ? {
    //       streamUrl: 'https://tv-trtworld.medya.trt.com.tr/master.m3u8',
    //     }
    //   : item?.liveStream,
    title: contentMetadata?.title ?? content?.title,
    id: contentMetadata?.id,
    duration: content?.duration,
    slug: content?.slug,
    type: content?.type,
    subType: content?.subType,
    ...(content?.tvShow ? { tvShow: content?.tvShow } : {}),
  } as ContentMetadata;
};

export const getNavigatorConnection = (): any => {
  if (
    'connection' in navigator ||
    'mozConnection' in navigator ||
    'webkitConnection' in navigator
  ) {
    return 'connection' in navigator
      ? navigator.connection
      : 'mozConnection' in navigator
        ? navigator.mozConnection
        : 'webkitConnection' in navigator
          ? navigator.webkitConnection
          : null;
  } else {
    return;
  }
};

export const internetAvailableForVideo = () => {
  if (typeof window === 'undefined') return false;
  const connection = getNavigatorConnection();
  if (!connection) return true;
  return true;
  // return !(
  //   connection?.effectiveType === 'slow-2g' ||
  //   connection?.effectiveType === '2g' ||
  //   connection?.effectiveType === '3g'
  // );
};

export const getValidMenuItem = (menu: MenuItem) => {
  return (
    (menu.linkType === 'custom' && menu.url) ||
    (menu.linkType === 'live_coverage' && menu.liveCoverage?.id) ||
    (menu.linkType === 'content' && menu.content) ||
    (menu.linkType === 'topic' && menu?.slug) ||
    (menu.linkType === 'page' && menu.page?.slug) ||
    menu.slug
  );
};

export function getMenuItemLink(menu: MenuItem) {
  if (menu.linkType === 'custom' && menu.url) {
    return menu.url;
  }
  if (menu.linkType === 'live_coverage' && menu.liveCoverage?.id) {
    return `/live/${(menu.liveCoverage as unknown as Content)?.id}`;
  }
  if (menu.linkType === 'content' && (menu.content as Content)?.slug) {
    const type = (menu.content as Content).type;
    const slug = (menu.content as Content).slug;
    let linkType = 'article';
    if (type === 'audio') {
      linkType = 'audio';
    } else if (type === 'video') {
      linkType = 'video';
    }
    return `/${linkType}/${slug}`;
  }
  if (menu.linkType === 'topic' && (menu?.topic as Topic)?.page?.slug) {
    return `/${(menu.topic as Topic).page?.slug}`;
  }
  if (menu.linkType === 'page' && menu.page?.slug) {
    return `/${menu.page.slug}`;
  }
  if (menu.slug) {
    return `/${menu.slug}`;
  }
}

export function getValidMenuItemsFromMenu(menu?: Menu) {
  const menuItems = dedupeById(
    [
      ...(menu?.primaryMenuItems || []),
      ...(menu?.secondaryMenuItems || []),
    ]?.map(item => item as MenuItem),
  );

  return (
    menuItems.filter((item: MenuItem) => {
      if (getValidMenuItem(item)) {
        return item;
      }
    }) ?? []
  );
}

function pad(input: number) {
  return input < 10 ? '0' + input : input;
}

export function getContentDurationString(duration?: string | number) {
  if (!duration) return;
  if (
    typeof duration === 'string' &&
    (duration as string).includes(':') &&
    duration !== '00:00'
  ) {
    return duration;
  }
  if (duration && !isNaN(+duration)) {
    return `${pad(Math.floor((+duration % 3600) / 60))}:${pad(Math.floor(+duration % 60))}`;
  }
}
